/**
 * Hasta cuántos de un análisis de Reactivación pueden recibir por WhatsApp oficial (`oficialApi.alcanceAnalisis`).
 *
 * Lo usan Reactivación (el reporte y «Por dónde y cuándo») y la Audiencia del asistente oficial: los tres piden la
 * misma cuenta, así el número no cambia de una pantalla a la otra. Se pide una vez por análisis; si cambia el id, la
 * respuesta del anterior se descarta (no pinta el número de otro análisis).
 *
 * Un error no frena nada: la cuenta es para avisar antes, y el chequeo del backend sigue siendo la defensa. Quien
 * llama decide qué mostrar con `error`.
 *
 * `excluir` (29/10/2026, el paso Audiencia del asistente): las exclusiones elegidas con un análisis de Activa. Con ellas
 * la cuenta es la del equipo (`alcanceAnalisisConExcluir`) y se vuelve a pedir cuando cambian; las listas que valen para
 * todas las campañas las descuenta el backend en las dos.
 */

import { useEffect, useState } from "react";
import { oficialApi } from "../api";
import type { AlcanceAnalisis, ExcluirAudiencia } from "../types";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";

export interface EstadoAlcance {
  alcance: AlcanceAnalisis | null;
  /** hay un id y todavía no volvió su respuesta */
  cargando: boolean;
  error: string;
}

/**
 * `version`: cuando cambia, se vuelve a pedir la cuenta del mismo análisis. La usa quien cambia el reparto entre QR y
 * API (`ActivaCampaign.reparto`): con el análisis repartido, este canal cuenta sólo a los de la API.
 */
export function useAlcanceOficial(
  campanaActivaId: string | null | undefined,
  version?: string | number | null,
  excluir?: ExcluirAudiencia | null,
): EstadoAlcance {
  const [leido, setLeido] = useState<{ id: string; clave: string; alcance: AlcanceAnalisis | null; error: string } | null>(null);
  // las exclusiones como texto: un objeto nuevo con lo mismo no vuelve a pedir la cuenta
  const clave = excluir ? JSON.stringify(excluir) : "";

  useEffect(() => {
    if (!campanaActivaId) return;
    let vivo = true;
    const id = campanaActivaId;
    const conExcluir: ExcluirAudiencia | null = clave ? JSON.parse(clave) : null;
    // dentro del then: si la llamada lanza al toque, también cae en el catch y no rompe la pantalla
    Promise.resolve()
      .then(() => (conExcluir ? oficialApi.alcanceAnalisisConExcluir(id, conExcluir) : oficialApi.alcanceAnalisis(id)))
      .then((r) => {
        if (!vivo) return;
        const l = leerRespuesta<AlcanceAnalisis>(r);
        setLeido(l.ok && l.data ? { id, clave, alcance: l.data, error: "" } : { id, clave, alcance: null, error: l.ok ? "" : l.error || textoError(l) });
      })
      .catch((e) => { if (vivo) setLeido({ id, clave, alcance: null, error: textoError(leerError(e)) }); });
    return () => { vivo = false; };
  }, [campanaActivaId, version, clave]);

  const actual = campanaActivaId && leido?.id === campanaActivaId && leido.clave === clave ? leido : null;
  return { alcance: actual?.alcance ?? null, cargando: !!campanaActivaId && !actual, error: actual?.error ?? "" };
}
